<template>
    <div class="space-y-8">
        <div class="rounded-xl border border-gray-200 bg-white p-4">
            <div class="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h3 class="text-sm font-semibold text-gray-900 md:text-base">
                        {{ $t("checkout.guest.sectionTitle") }}
                    </h3>
                    <p class="text-xs text-gray-600 md:text-sm">
                        {{
                            $t("checkout.guest.roomComplete", {
                                done: completeCount,
                                total: roomForms.length,
                            })
                        }}
                    </p>
                </div>
                <div class="h-2 w-full rounded-full bg-gray-200 md:w-56">
                    <div
                        class="h-2 rounded-full bg-blue-600 transition-all"
                        :style="{ width: `${completionPercent}%` }"
                    />
                </div>
            </div>
        </div>

        <ApplyToAllToggle
            v-if="showApplyToAllToggle"
            v-model="applyToAll"
            :totalRooms="roomForms.length"
        />

        <div
            v-if="showCheckoutError"
            class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
            {{ $t("checkout.guest.incompleteWarning") }}
        </div>

        <div
            v-if="incompleteRooms.length > 0"
            class="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
        >
            <p class="text-xs font-medium text-amber-800 md:text-sm">
                {{ $t("checkout.guest.incompleteRooms") }}
            </p>
            <div class="mt-2 flex flex-wrap gap-2">
                <button
                    v-for="item in incompleteRooms"
                    :key="item.index"
                    type="button"
                    class="rounded-full border border-amber-300 bg-white px-3 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100"
                    @click="scrollToRoom(item.index)"
                >
                    {{ $t("checkout.guest.room") }} {{ item.index + 1 }}
                </button>
            </div>
        </div>

        <div class="space-y-8">
            <RoomGuestCard
                v-for="(room, idx) in roomForms"
                :key="room.roomKey"
                :ref="`roomCard-${idx}`"
                :roomKey="room.roomKey"
                :roomIndex="idx"
                :roomTitle="room.roomTitle"
                :propertyName="room.propertyName"
                :guestCapacity="room.guestCapacity"
                :roomSize="room.roomSize"
                :roomImage="room.roomImage"
                :form="room.form"
                :errors="room.errors"
                :visualState="room.visualState"
                :lastSavedLabel="room.lastSavedLabel"
                :showSaveAction="showApplyToAllToggle"
                :isSaving="Boolean(savingRoomKeys[room.roomKey])"
                @update:form="updateRoomForm(idx, $event)"
                @save="saveRoom(idx)"
            />
        </div>
    </div>
</template>

<script>
import ApplyToAllToggle from "@/components/modules/ApplyToAllToggle.vue";
import RoomGuestCard from "@/components/modules/RoomGuestCard.vue";
import { API_URL } from "@/mainconfig";
import { isValidPersonName } from "@/utils/nameValidation";
import { isCompletePhoneNumber } from "@/utils/phoneValidation.mjs";
import { isPassportIdentity, isValidIdentityNumber } from "@/utils/identityNumber.mjs";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default {
    name: "GuestDataSection",
    components: {
        ApplyToAllToggle,
        RoomGuestCard,
    },
    props: {
        rooms: {
            type: Array,
            default: () => [],
        },
        profile: {
            type: Object,
            default: () => ({
                id: "",
                isLoggedIn: false,
                name: "",
                email: "",
                phone: "",
                identityType: "",
                identityNumber: "",
                identityImageId: null,
                identityImageFileName: "",
                identityImagePreviewUrl: "",
                emergencyContactName: "",
                emergencyContactPhone: "",
                emergencyContactRelation: "",
                emergencyContactRelationOther: "",
            }),
        },
    },
    data() {
        return {
            applyToAll: false,
            applyToAllBackupByRoomKey: {},
            roomForms: [],
            showCheckoutError: false,
            savingRoomKeys: {},
        };
    },
    computed: {
        completeCount() {
            return this.roomForms.filter((room) => this.isRoomComplete(room.form)).length;
        },
        completionPercent() {
            if (!this.roomForms.length) return 0;
            return Math.round((this.completeCount / this.roomForms.length) * 100);
        },
        allRoomsValid() {
            return this.roomForms.length > 0 && this.roomForms.every((room) => this.isRoomComplete(room.form));
        },
        incompleteRooms() {
            return this.roomForms
                .map((room, index) => ({ room, index }))
                .filter(({ room }) => !this.isRoomComplete(room.form));
        },
        showApplyToAllToggle() {
            return this.roomForms.length > 1;
        },
    },
    watch: {
        rooms: {
            immediate: true,
            handler(list) {
                this.buildInitialForms(list || []);
            },
        },
        profile: {
            deep: true,
            handler() {
                if (!this.roomForms.length) return;
                this.buildInitialForms(this.rooms || []);
            },
        },
        allRoomsValid: {
            immediate: true,
            handler(value) {
                this.$emit("update:allValid", value);
            },
        },
        roomForms: {
            deep: true,
            handler() {
                const firstRoom = this.roomForms[0]?.form;
                this.$emit("update:primaryGuest", {
                    name: firstRoom?.guestName || "",
                    email: firstRoom?.email || "",
                    phone: firstRoom?.phone || "",
                });
            },
        },
        applyToAll(value) {
            if (!this.showApplyToAllToggle && this.applyToAll) {
                this.applyToAll = false;
                return;
            }
            if (value && this.showApplyToAllToggle) {
                this.captureApplyToAllBackup();
                this.copyFirstRoomToAll();
                return;
            }
            if (!value && this.showApplyToAllToggle) {
                this.restoreApplyToAllBackup();
            }
        },
    },
    methods: {
        cloneForm(form) {
            return JSON.parse(JSON.stringify(form || {}));
        },
        captureApplyToAllBackup() {
            const backup = {};
            this.roomForms.forEach((room, idx) => {
                if (idx === 0) return;
                backup[room.roomKey] = this.cloneForm(room.form);
            });
            this.applyToAllBackupByRoomKey = backup;
        },
        restoreApplyToAllBackup() {
            if (!this.applyToAllBackupByRoomKey) return;

            const hasBackup = Object.keys(this.applyToAllBackupByRoomKey).length > 0;
            if (!hasBackup) return;

            const restored = this.roomForms.map((room, idx) => {
                if (idx === 0) return room;

                const backupForm = this.applyToAllBackupByRoomKey[room.roomKey];
                if (!backupForm) return room;

                const nextVisualState =
                    backupForm.identityUpload?.uploadState === "uploading"
                        ? "uploading"
                        : this.isRoomComplete(backupForm)
                        ? "complete"
                        : "incomplete";

                return {
                    ...room,
                    form: this.cloneForm(backupForm),
                    errors: {},
                    visualState: nextVisualState,
                };
            });

            this.roomForms = restored;
            this.applyToAllBackupByRoomKey = {};
        },
        buildImageUrl(imageId, original = false) {
            if (!imageId) return "";
            const baseUrl = String(API_URL?.defaults?.baseURL || "").replace(
                /\/$/,
                ""
            );
            if (!baseUrl) return "";
            return `${baseUrl}/image/${imageId}${
                original ? "?original=true" : ""
            }`;
        },
        normalizeInternationalPhone(value) {
            const raw = String(value || "");
            const clean = raw.replace(/[^+\d]/g, "");
            if (!clean) return "";

            if (clean.startsWith("+")) return `+${clean.slice(1).replace(/\D/g, "")}`;
            if (clean.startsWith("62")) return `+${clean.replace(/\D/g, "")}`;
            if (clean.startsWith("0")) return `+62${clean.slice(1).replace(/\D/g, "")}`;

            return `+62${clean.replace(/\D/g, "")}`;
        },
        normalizeRelationValue(value) {
            if (value === undefined || value === null || value === "") return "";
            if (typeof value === "number") {
                const codeMap = {
                    1: "PARENT",
                    2: "SPOUSE",
                    3: "CHILD",
                    4: "SIBLING",
                    5: "FRIEND",
                    9: "OTHER",
                };
                return codeMap[value] || "";
            }
            const text = String(value).trim().toUpperCase();
            const textMap = {
                PARENT: "PARENT",
                SPOUSE: "SPOUSE",
                CHILD: "CHILD",
                SIBLING: "SIBLING",
                FRIEND: "FRIEND",
                OTHER: "OTHER",
            };
            return textMap[text] || "";
        },
        mapRelationToCode(value) {
            if (value === undefined || value === null || value === "") {
                return null;
            }
            if (typeof value === "number") {
                return [1, 2, 3, 4, 5, 9].includes(value) ? value : null;
            }
            const normalized = String(value).trim().toUpperCase();
            const relationMap = {
                PARENT: 1,
                SPOUSE: 2,
                CHILD: 3,
                SIBLING: 4,
                FRIEND: 5,
                OTHER: 9,
            };
            return relationMap[normalized] ?? null;
        },
        buildUpdateCartDetailPayload(roomEntry) {
            const form = roomEntry?.form || {};
            const emergency = form?.emergency || {};
            const relation = this.mapRelationToCode(emergency?.relation);
            const identityImageId =
                form?.identityImageId ?? form?.identityUpload?.imageId ?? null;

            return {
                CartDetailId: roomEntry.roomKey,
                GuestName: form?.guestName?.trim() || null,
                GuestEmail: form?.email?.trim() || null,
                GuestPhoneNumber: this.normalizeInternationalPhone(
                    form?.phone || ""
                ),
                GuestIdentityType: form?.identityType?.trim()
                    ? String(form.identityType).trim().toUpperCase()
                    : null,
                GuestIdentityNumber: form?.identityNumber?.trim() || null,
                GuestIdentityImageId:
                    identityImageId !== null &&
                    identityImageId !== undefined &&
                    identityImageId !== ""
                        ? Number(identityImageId)
                        : null,
                GuestEmergencyContactName: emergency?.name?.trim() || null,
                GuestEmergencyContactPhone: this.normalizeInternationalPhone(
                    emergency?.phone || ""
                ),
                GuestEmergencyContactRelation: relation,
                GuestEmergencyContactRelationOther:
                    relation === 9
                        ? emergency?.relationOther?.trim() || null
                        : null,
            };
        },
        buildInitialForms(rooms) {
            this.applyToAllBackupByRoomKey = {};
            const existingByKey = new Map(
                this.roomForms.map((room) => [room.roomKey, room])
            );

            this.roomForms = rooms.map((room, idx) => {
                const roomKey = room?.Id || room?.RoomId || idx;
                const snapshot = this.extractGuestSnapshot(room);
                const hasSnapshot = this.hasSnapshotData(snapshot);
                const existing = existingByKey.get(roomKey);

                // Jangan override data yang sudah disimpan user, kecuali backend sudah kirim snapshot.
                if (
                    existing &&
                    !hasSnapshot &&
                    this.wasSavedByUser(existing)
                ) {
                    return {
                        ...existing,
                        roomTitle:
                            room?.Room?.Name ||
                            room?.RoomName ||
                            `${this.$t("checkout.guest.room")} ${idx + 1}`,
                        propertyName:
                            room?.PropertyName ||
                            room?.Property?.Name ||
                            existing?.propertyName ||
                            "",
                        guestCapacity:
                            room?.Pax ??
                            room?.Room?.Pax ??
                            existing?.guestCapacity ??
                            null,
                        roomSize:
                            room?.Room?.SizeRoom ||
                            room?.SizeRoom ||
                            existing?.roomSize ||
                            "",
                        roomImage:
                            room?.Images?.[0]?.Filename ||
                            room?.Room?.Images?.[0]?.Filename ||
                            existing?.roomImage ||
                            "",
                    };
                }

                const prefill = this.buildRoomPrefill(snapshot, hasSnapshot, idx);

                return {
                    roomKey,
                    roomTitle: room?.Room?.Name || room?.RoomName || `${this.$t("checkout.guest.room")} ${idx + 1}`,
                    propertyName:
                        room?.PropertyName || room?.Property?.Name || "",
                    guestCapacity: room?.Pax ?? room?.Room?.Pax ?? null,
                    roomSize: room?.Room?.SizeRoom || room?.SizeRoom || "",
                    roomImage:
                        room?.Images?.[0]?.Filename ||
                        room?.Room?.Images?.[0]?.Filename ||
                        "",
                    form: prefill,
                    errors: {},
                    visualState: this.isRoomComplete(prefill) ? "complete" : "incomplete",
                    // null = belum pernah disimpan user. Sentinel-nya BUKAN teks tampilan:
                    // wasSavedByUser() membacanya, jadi kalau diisi string berbahasa,
                    // ganti bahasa membuat room lama dikira sudah tersimpan.
                    lastSavedLabel: null,
                };
            });
        },
        extractGuestSnapshot(room) {
            // fallback key agar aman dengan shape data backend/dummy yang berbeda
            const snap = room?.GuestSnapshot || room?.GuestInfo || room?.Guest || {};
            const identity = snap?.Identity || {};
            const emergency = snap?.EmergencyContact || {};
            return {
                guestName: snap?.GuestName || snap?.Name || room?.GuestName || "",
                email: snap?.GuestEmail || snap?.Email || room?.GuestEmail || "",
                phone:
                    snap?.GuestPhoneNumber ||
                    snap?.PhoneNumber ||
                    room?.GuestPhoneNumber ||
                    "",
                identityType:
                    snap?.GuestIdentityType ||
                    identity?.Type ||
                    room?.GuestIdentityType ||
                    "",
                identityNumber:
                    snap?.GuestIdentityNumber ||
                    identity?.Number ||
                    room?.GuestIdentityNumber ||
                    "",
                identityImageId:
                    snap?.GuestIdentityImageId ??
                    identity?.ImageId ??
                    room?.GuestIdentityImageId ??
                    null,
                identityFileName:
                    snap?.GuestIdentityFileName ||
                    identity?.FileName ||
                    room?.GuestIdentityFileName ||
                    "",
                identityPreviewUrl:
                    snap?.GuestIdentityPreviewUrl ||
                    identity?.PreviewUrl ||
                    room?.GuestIdentityPreviewUrl ||
                    "",
                emergencyName:
                    snap?.GuestEmergencyContactName ||
                    emergency?.Name ||
                    room?.GuestEmergencyContactName ||
                    "",
                emergencyPhone:
                    snap?.GuestEmergencyContactPhone ||
                    emergency?.PhoneNumber ||
                    room?.GuestEmergencyContactPhone ||
                    "",
                emergencyRelation:
                    this.normalizeRelationValue(
                        snap?.GuestEmergencyContactRelation ||
                            emergency?.Relation ||
                            room?.GuestEmergencyContactRelation ||
                            ""
                    ) || "",
                emergencyRelationOther:
                    snap?.GuestEmergencyContactRelationOther ||
                    emergency?.RelationOther ||
                    room?.GuestEmergencyContactRelationOther ||
                    "",
            };
        },
        hasSnapshotData(snapshot) {
            const relation = String(snapshot?.emergencyRelation || "").toUpperCase();
            return Boolean(
                (snapshot?.guestName || "").trim() ||
                    (snapshot?.email || "").trim() ||
                    (snapshot?.phone || "").trim() ||
                    (snapshot?.identityNumber || "").trim() ||
                    snapshot?.identityImageId ||
                    (snapshot?.emergencyName || "").trim() ||
                    (snapshot?.emergencyPhone || "").trim() ||
                    (snapshot?.emergencyRelation || "").trim() ||
                    (relation === "OTHER" &&
                        (snapshot?.emergencyRelationOther || "").trim())
            );
        },
        wasSavedByUser(room) {
            return (
                room?.visualState === "saved" || !!room?.lastSavedLabel
            );
        },
        buildRoomPrefill(snapshot, hasSnapshot, roomIndex = 0) {
            const profile = this.profile || {};
            const useProfile =
                !hasSnapshot &&
                !!profile.isLoggedIn &&
                Number(roomIndex) === 0;
            const identityImageId = hasSnapshot
                ? snapshot.identityImageId ?? null
                : useProfile
                ? profile.identityImageId ?? null
                : null;
            const previewFromImageId = this.buildImageUrl(identityImageId);

            const identityPreviewUrl = useProfile
                ? profile.identityImagePreviewUrl || ""
                : snapshot.identityPreviewUrl || previewFromImageId;
            const identityFileName = useProfile
                ? profile.identityImageFileName || ""
                : snapshot.identityFileName || "";
            const profileIdentityImageId = useProfile
                ? profile.identityImageId
                : null;

            const hasUploadedIdentity = hasSnapshot
                ? Boolean(
                      snapshot.identityFileName ||
                          snapshot.identityPreviewUrl ||
                          snapshot.identityImageId
                  )
                : Boolean(
                      profileIdentityImageId ||
                          identityFileName ||
                          identityPreviewUrl
                  );

            const emergencyRelation = useProfile
                ? this.normalizeRelationValue(profile.emergencyContactRelation)
                : this.normalizeRelationValue(snapshot.emergencyRelation);

            const phoneValue = this.normalizeInternationalPhone(
                hasSnapshot
                    ? snapshot.phone || ""
                    : useProfile
                    ? profile.phone || ""
                    : ""
            );
            const profileEmergencyPhoneValue = this.normalizeInternationalPhone(
                profile.emergencyContactPhone || ""
            );
            const emergencyPhoneValue = this.normalizeInternationalPhone(
                profile.isLoggedIn && profileEmergencyPhoneValue
                    ? profileEmergencyPhoneValue
                    : hasSnapshot
                    ? snapshot.emergencyPhone || ""
                    : useProfile
                    ? profile.emergencyContactPhone || ""
                    : ""
            );

            return {
                guestName: hasSnapshot
                    ? snapshot.guestName || ""
                    : useProfile
                    ? profile.name || ""
                    : "",
                email: hasSnapshot
                    ? snapshot.email || ""
                    : useProfile
                    ? profile.email || ""
                    : "",
                phone: phoneValue,
                identityType: hasSnapshot
                    ? snapshot.identityType || ""
                    : useProfile
                    ? profile.identityType || ""
                    : "",
                identityNumber: hasSnapshot
                    ? snapshot.identityNumber || ""
                    : useProfile
                    ? profile.identityNumber || ""
                    : "",
                identityImageId,
                identityUpload: {
                    fileName:
                        identityFileName ||
                        (hasUploadedIdentity ? "Uploaded identity" : ""),
                    previewUrl: identityPreviewUrl || previewFromImageId || "",
                    originalUrl:
                        this.buildImageUrl(identityImageId, true) ||
                        identityPreviewUrl ||
                        previewFromImageId ||
                        "",
                    isImage: true,
                    uploadState: hasUploadedIdentity ? "success" : "idle",
                    errorMessage: "",
                },
                emergency: {
                    name: hasSnapshot
                        ? snapshot.emergencyName || ""
                        : useProfile
                        ? profile.emergencyContactName || ""
                        : "",
                    phone: emergencyPhoneValue,
                    relation: emergencyRelation,
                    relationOther:
                        String(emergencyRelation).toUpperCase() === "OTHER"
                            ? hasSnapshot
                                ? snapshot.emergencyRelationOther || ""
                                : useProfile
                                ? profile.emergencyContactRelationOther || ""
                                : ""
                            : "",
                },
            };
        },
        isRoomComplete(form) {
            if (!form) return false;
            const hasIdentityUpload = form.identityUpload?.uploadState === "success";
            const relationOtherValid =
                form.emergency.relation !== "OTHER" ||
                (form.emergency.relationOther || "").trim().length > 0;

            return (
                (form.guestName || "").trim().length >= 3 &&
                EMAIL_REGEX.test(form.email || "") &&
                isCompletePhoneNumber(form.phone) &&
                isValidIdentityNumber(form.identityNumber, form.identityType) &&
                !!form.identityType &&
                hasIdentityUpload &&
                (form.emergency.name || "").trim().length >= 3 &&
                isCompletePhoneNumber(form.emergency.phone) &&
                !!form.emergency.relation &&
                relationOtherValid
            );
        },
        validateForm(form) {
            const errors = {};
            if (!(form.guestName || "").trim()) errors.guestName = "Guest name wajib diisi";
            else if ((form.guestName || "").trim().length < 3) errors.guestName = "Minimal 3 karakter";
            else if (!isValidPersonName(form.guestName)) errors.guestName = "Nama hanya boleh huruf, angka, spasi, dan tanda . ' -";

            if (!EMAIL_REGEX.test(form.email || "")) errors.email = "Format email tidak valid";
            if (!isCompletePhoneNumber(form.phone)) errors.phone = "Nomor HP belum lengkap";

            if (!(form.identityNumber || "").trim()) errors.identityNumber = "Identity number wajib diisi";
            else if (!isValidIdentityNumber(form.identityNumber, form.identityType)) {
                errors.identityNumber = isPassportIdentity(form.identityType)
                    ? "Identity number hanya boleh huruf dan angka"
                    : "Identity number hanya boleh angka";
            }
            if (!form.identityType) errors.identityType = "Identity type wajib dipilih";
            if (form.identityUpload?.uploadState !== "success") errors.identityUpload = "Dokumen identitas wajib diupload";

            if (!(form.emergency.name || "").trim()) errors.emergencyName = "Nama emergency contact wajib diisi";
            else if (!isValidPersonName(form.emergency.name)) errors.emergencyName = "Nama hanya boleh huruf, angka, spasi, dan tanda . ' -";
            if (!isCompletePhoneNumber(form.emergency.phone)) errors.emergencyPhone = "Nomor HP belum lengkap";
            if (!form.emergency.relation) errors.emergencyRelation = "Relation wajib dipilih";
            if (
                form.emergency.relation === "OTHER" &&
                !(form.emergency.relationOther || "").trim()
            ) {
                errors.emergencyRelationOther = "Relation other wajib diisi";
            }

            return errors;
        },
        updateRoomForm(index, nextForm) {
            const next = [...this.roomForms];
            const current = next[index];
            if (!current) return;

            const normalizedForm = {
                ...nextForm,
                phone: this.normalizeInternationalPhone(nextForm?.phone || ""),
                emergency: {
                    ...(nextForm?.emergency || {}),
                    phone: this.normalizeInternationalPhone(
                        nextForm?.emergency?.phone || ""
                    ),
                },
            };

            const visualState =
                normalizedForm.identityUpload?.uploadState === "uploading"
                    ? "uploading"
                    : this.isRoomComplete(normalizedForm)
                    ? "complete"
                    : "incomplete";

            next[index] = {
                ...current,
                form: normalizedForm,
                errors:
                    current.visualState === "error"
                        ? this.validateForm(normalizedForm)
                        : {},
                visualState,
            };

            this.roomForms = next;

            if (this.applyToAll && index === 0) {
                this.copyFirstRoomToAll();
            }
        },
        createSyncedGuestForm(sourceForm, targetForm) {
            const sourceEmergency = sourceForm?.emergency || {};
            const targetEmergency = targetForm?.emergency || {};

            return {
                ...targetForm,
                guestName: sourceForm?.guestName || "",
                email: sourceForm?.email || "",
                phone: this.normalizeInternationalPhone(
                    sourceForm?.phone || ""
                ),
                identityType: sourceForm?.identityType || "",
                identityNumber: sourceForm?.identityNumber || "",
                identityImageId: sourceForm?.identityImageId ?? null,
                identityUpload: sourceForm?.identityUpload
                    ? JSON.parse(JSON.stringify(sourceForm.identityUpload))
                    : targetForm?.identityUpload,
                emergency: {
                    ...targetEmergency,
                    name: sourceEmergency?.name || "",
                    phone: this.normalizeInternationalPhone(
                        sourceEmergency?.phone || ""
                    ),
                    relation: sourceEmergency?.relation || "",
                    relationOther:
                        String(sourceEmergency?.relation || "").toUpperCase() ===
                        "OTHER"
                            ? sourceEmergency?.relationOther || ""
                            : "",
                },
            };
        },
        copyFirstRoomToAll() {
            const first = this.roomForms[0];
            if (!first) return;
            const cloned = this.roomForms.map((room, idx) => {
                if (idx === 0) return room;
                const copiedForm = this.createSyncedGuestForm(
                    first.form,
                    room.form
                );
                return {
                    ...room,
                    form: copiedForm,
                    errors: {},
                    visualState:
                        copiedForm.identityUpload?.uploadState === "uploading"
                            ? "uploading"
                            : this.isRoomComplete(copiedForm)
                            ? "complete"
                            : "incomplete",
                };
            });
            this.roomForms = cloned;
        },
        async saveRoom(index) {
            const next = [...this.roomForms];
            const current = next[index];
            if (!current) return;

            const errors = this.validateForm(current.form);
            const hasError = Object.keys(errors).length > 0;

            if (hasError) {
                next[index] = {
                    ...current,
                    errors,
                    visualState: "error",
                };
                this.roomForms = next;
                return;
            }

            this.savingRoomKeys = {
                ...this.savingRoomKeys,
                [current.roomKey]: true,
            };

            next[index] = {
                ...current,
                errors: {},
                visualState: "uploading",
            };
            this.roomForms = next;

            try {
                const payload = this.buildUpdateCartDetailPayload(current);
                await API_URL.post("/booking/updateCartDetail", payload, {
                    requiresAuth: true,
                });

                const savedState = [...this.roomForms];
                if (!savedState[index]) return;
                savedState[index] = {
                    ...savedState[index],
                    errors: {},
                    visualState: "saved",
                    lastSavedLabel: new Date().toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                    }),
                };
                this.roomForms = savedState;

                this.$emit("saved:room", {
                    roomKey: current.roomKey,
                });

                setTimeout(() => {
                    const updated = [...this.roomForms];
                    if (!updated[index]) return;
                    updated[index] = {
                        ...updated[index],
                        visualState: this.isRoomComplete(updated[index].form)
                            ? "complete"
                            : "incomplete",
                    };
                    this.roomForms = updated;
                }, 1200);
            } catch (error) {
                const failedState = [...this.roomForms];
                if (failedState[index]) {
                    failedState[index] = {
                        ...failedState[index],
                        errors: {
                            ...failedState[index].errors,
                            _save:
                                error?.response?.data?.msg ||
                                "Gagal menyimpan data room.",
                        },
                        visualState: "error",
                    };
                    this.roomForms = failedState;
                }
            } finally {
                this.savingRoomKeys = {
                    ...this.savingRoomKeys,
                    [current.roomKey]: false,
                };
            }
        },
        scrollToFirstInvalid() {
            const invalidIndex = this.roomForms.findIndex((room) => !this.isRoomComplete(room.form));
            if (invalidIndex < 0) return true;

            this.showCheckoutError = true;

            const next = [...this.roomForms];
            next.forEach((room, idx) => {
                if (!this.isRoomComplete(room.form)) {
                    next[idx] = {
                        ...room,
                        visualState: "error",
                        errors: this.validateForm(room.form),
                    };
                }
            });
            this.roomForms = next;

            // Arahkan ke field pertama yang salah, bukan sekadar ke kartu room-nya.
            const firstField = Object.keys(next[invalidIndex].errors || {})[0];
            this.$nextTick(() => this.scrollToRoom(invalidIndex, firstField));
            return false;
        },
        scrollToRoom(index, fieldKey) {
            const ref = this.$refs[`roomCard-${index}`];
            const card = Array.isArray(ref) ? ref[0] : ref;
            if (!card) return;
            if (fieldKey && card.focusError) {
                card.focusError(fieldKey);
                return;
            }
            card.$el?.scrollIntoView({ behavior: "smooth", block: "center" });
        },
        validateBeforeCheckout() {
            const isValid = this.allRoomsValid;
            if (isValid) {
                this.showCheckoutError = false;
                return true;
            }
            return this.scrollToFirstInvalid();
        },
        getRoomForms() {
            return this.roomForms.map((room) => ({
                roomKey: room.roomKey,
                form: JSON.parse(JSON.stringify(room.form || {})),
            }));
        },
    },
};
</script>
